import { Input } from "@/components/ui/input";
import { User, Mail, Phone, MapPin, Edit2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "../ui/button";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";

interface ContactPerson {
  name: string;
  email: string;
  phone: string;
}

interface Location {
  address: string;
  city: string;
  country: string;
}

interface EnterpriseContactInfoProps {
  //   isEditing: boolean;
  formData: {
    contactPerson: ContactPerson;
    location: Location;
  };
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  updateEnterpriseProfile: (
    id: string,
    data: Partial<any>,
    token: string
  ) => Promise<void>;
  handleCancel?: () => void;
  isClientProfile:boolean
}

export const EnterpriseContactInfo = ({
  formData,
  handleInputChange,
  updateEnterpriseProfile,
  handleCancel,
  isClientProfile
}: EnterpriseContactInfoProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();
  const { user } = useUserStore();

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!user?.accessToken || !user?.userId) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile.",
        variant: "destructive",
      });
      return;
    }

    if (
      !formData.contactPerson.name ||
      !formData.contactPerson.email ||
      !formData.contactPerson.phone ||
      !formData.location.address ||
      !formData.location.city ||
      !formData.location.country
    ) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const updatedData = {
      ...formData,
      location: formData.location,
      contactPerson: formData.contactPerson,
    };

    try {
      await updateEnterpriseProfile(
        user?.userId,
        updatedData,
        user?.accessToken
      );
      setIsEditing(false);
      toast({
        title: "Profile Updated",
        description: "Your enterprise profile has been successfully updated.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="hover:shadow-md transition-all duration-300 border-blue-100">
      <CardHeader className="pb-2">
        <div className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-xl flex items-center gap-2">
            Contact Information
          </CardTitle>
          {!isEditing && !isClientProfile && (
            <Button variant="ghost" size="sm" onClick={handleEditClick}>
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <Input
                  name="contactPerson.name"
                  value={formData.contactPerson.name}
                  onChange={handleInputChange}
                  className="w-full"
                  placeholder="Contact person name"
                />
              </div>
            ) : (
              formData.contactPerson.name && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Contact Person:</span>
                  <span className="text-gray-600">
                    {formData.contactPerson.name}
                  </span>
                </div>
              )
            )}

            {isEditing ? (
              <></>
              // <div className="flex items-center gap-2">
              //   <Mail className="w-4 h-4 text-gray-500" />
              //   <Input
              //     name="contactPerson.email"
              //     value={formData.contactPerson.email}
              //     onChange={handleInputChange}
              //     className="w-full"
              //     placeholder="Contact email"
              //     readOnly
              //   />
              // </div>
            ) : (
              formData.contactPerson.email && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Email:</span>
                  <span className="text-gray-600">
                    {formData.contactPerson.email}
                  </span>
                </div>
              )
            )}

            {isEditing ? (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-500" />
                <Input
                  name="contactPerson.phone"
                  value={formData.contactPerson.phone}
                  onChange={handleInputChange}
                  className="w-full"
                  placeholder="Contact phone"
                />
              </div>
            ) : (
              formData.contactPerson.phone && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Phone:</span>
                  <span className="text-gray-600">
                    {formData.contactPerson.phone}
                  </span>
                </div>
              )
            )}

            {isEditing ? (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <Input
                  name="location.address"
                  value={formData.location.address}
                  onChange={handleInputChange}
                  className="w-full"
                  placeholder="Address"
                />
              </div>
            ) : (
              formData.location.address && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Address:</span>
                  <span className="text-gray-600">
                    {formData.location.address}
                  </span>
                </div>
              )
            )}

            {isEditing ? (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <Input
                  name="location.city"
                  value={formData.location.city}
                  onChange={handleInputChange}
                  className="w-full"
                  placeholder="City"
                />
              </div>
            ) : (
              formData.location.city && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">City:</span>
                  <span className="text-gray-600">
                    {formData.location.city}
                  </span>
                </div>
              )
            )}

            {isEditing ? (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <Input
                  name="location.country"
                  value={formData.location.country}
                  onChange={handleInputChange}
                  className="w-full"
                  placeholder="Country"
                />
              </div>
            ) : (
              formData.location.country && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Country:</span>
                  <span className="text-gray-600">
                    {formData.location.country}
                  </span>
                </div>
              )
            )}

            {isEditing && (
              <div className="mt-2">
                <Button className="bg-blue-500 text-white" onClick={handleSave}>
                  Save
                </Button>
                <Button
                  className="bg-blue-500 text-white mx-2"
                  onClick={()=>{
                    handleEditClick()
                    handleCancel()
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
